import { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Lock, User, KeyRound, Factory, MapPin, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { storeLoginData, setCurrentPlantId } from '../utils/sessionManager';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UnifiedLogin = () => {
  const navigate = useNavigate();
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Password change dialog state
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [passwordChangeData, setPasswordChangeData] = useState({
    user_id: '',
    new_password: '',
    confirm_password: ''
  });
  const [passwordChangeLoading, setPasswordChangeLoading] = useState(false);
  const [tempUserData, setTempUserData] = useState(null);
  
  // Session exists dialog state
  const [showSessionExistsDialog, setShowSessionExistsDialog] = useState(false);
  const [sessionExistsLoading, setSessionExistsLoading] = useState(false);

  // Plant selection state
  const [showPlantSelection, setShowPlantSelection] = useState(false);
  const [availablePlants, setAvailablePlants] = useState([]);
  const [pendingLoginData, setPendingLoginData] = useState(null);

  const finalizeLogin = (data, selectedPlantId) => {
    storeLoginData(data, false);
    setCurrentPlantId(selectedPlantId);
    toast.success(`Welcome, ${data.user.username}!`);
    navigate('/');
    window.location.reload();
  };

  const completeLogin = async (data, isVendor = false) => {
    if (isVendor) {
      // Vendors skip plant selection
      storeLoginData(data, true);
      toast.success(`Welcome, ${data.user.username}!`);
      navigate('/');
      window.location.reload();
      return;
    }

    const userPlants = data.user?.accessible_plants || [];
    const allPlants = data.plants || [];

    // Filter available plants to only those the user has access to
    const userAvailablePlants = allPlants.filter(p => userPlants.includes(p.id));

    if (userAvailablePlants.length === 0) {
      // No plants assigned — use the user's default plant or first available
      storeLoginData(data, false);
      toast.success(`Welcome, ${data.user.username}!`);
      navigate('/');
      window.location.reload();
      return;
    }

    if (userAvailablePlants.length === 1) {
      // Only one plant — auto-select it
      finalizeLogin(data, userAvailablePlants[0].id);
      return;
    }

    // Multiple plants — show plant selection screen
    setAvailablePlants(userAvailablePlants);
    setPendingLoginData(data);
    setShowPlantSelection(true);
  };

  const handlePlantSelect = (plantId) => {
    if (!pendingLoginData) return;
    setShowPlantSelection(false);
    finalizeLogin(pendingLoginData, plantId);
  };

  const handlePasswordChange = async () => {
    if (passwordChangeData.new_password !== passwordChangeData.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (passwordChangeData.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setPasswordChangeLoading(true);
    
    try {
      await axios.post(`${API}/auth/change-password`, passwordChangeData);
      
      toast.success('Password changed successfully!');
      setShowPasswordChange(false);
      
      const updatedUserData = {
        ...tempUserData,
        user: {
          ...tempUserData.user,
          must_change_password: false
        }
      };
      
      completeLogin(updatedUserData);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setPasswordChangeLoading(false);
    }
  };

  const handleLogin = async (e, forceLogoutOthers = false) => {
    if (e) e.preventDefault();
    setError('');
    setLoading(true);

    try {
      try {
        const response = await axios.post(`${API}/auth/login`, {
          ...credentials,
          force_logout_others: forceLogoutOthers
        });
        
        if (response.data.status === 'session_exists' && response.data.requires_confirmation) {
          setShowSessionExistsDialog(true);
          setLoading(false);
          return;
        }
        
        if (response.data.user?.must_change_password) {
          setTempUserData({
            user: response.data.user,
            role: response.data.role,
            session_token: response.data.session_token,
            plants: response.data.plants
          });
          setPasswordChangeData({
            ...passwordChangeData,
            user_id: response.data.user.id
          });
          setShowPasswordChange(true);
          setLoading(false);
          return;
        }
        
        // Pass full response including plants list
        completeLogin({
          user: response.data.user,
          role: response.data.role,
          session_token: response.data.session_token,
          plants: response.data.plants
        });
        
        return;
      } catch (userError) {
        if (userError.response?.status === 401) {
          const vendorResponse = await axios.post(`${API}/vendor-auth/login`, null, {
            params: { 
              username: credentials.username, 
              password: credentials.password 
            }
          });

          const vendorRole = {
            name: 'Vendor',
            permissions: {
              schedule: { view: true, create: false, edit: false, delete: false },
              procurement: { view: true, create: false, edit: false, delete: false },
              shipment: { view: true, create: false, edit: false, delete: false },
              grn: { view: true, create: false, edit: false, delete: false },
              transactions: { view: true, create: false, edit: false, delete: false }
            }
          };

          const vendorUser = {
            id: vendorResponse.data.vendor.id,
            username: vendorResponse.data.vendor.username || vendorResponse.data.vendor.name,
            email: vendorResponse.data.vendor.email,
            role_id: 'vendor',
            vendor_id: vendorResponse.data.vendor.id
          };

          completeLogin({
            user: vendorUser,
            role: vendorRole,
            vendor: vendorResponse.data.vendor
          }, true);
          
          return;
        } else {
          throw userError;
        }
      }
      
    } catch (error) {
      setError('Invalid username or password. Please try again.');
      toast.error('Login failed');
    } finally {
      setLoading(false);
    }
  };
  
  const handleForceLogin = async () => {
    setSessionExistsLoading(true);
    setShowSessionExistsDialog(false);
    await handleLogin(null, true);
    setSessionExistsLoading(false);
  };

  const handleInputChange = (e) => {
    setCredentials({
      ...credentials,
      [e.target.name]: e.target.value
    });
  };

  // Plant Selection Screen
  if (showPlantSelection) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
        <Card className="w-full max-w-lg" data-testid="plant-selection-card">
          <CardHeader className="space-y-1 text-center">
            <div className="flex items-center justify-center mb-4">
              <div className="bg-teal-600 p-4 rounded-full">
                <Factory className="w-8 h-8 text-white" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold">Select Plant</CardTitle>
            <CardDescription>
              Welcome, <strong>{pendingLoginData?.user?.username}</strong>! Choose a plant to continue.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 pb-8">
            {availablePlants.map(plant => (
              <button
                key={plant.id}
                data-testid={`select-plant-${plant.id}`}
                onClick={() => handlePlantSelect(plant.id)}
                className="w-full flex items-center gap-4 p-4 border-2 border-slate-200 rounded-xl hover:border-teal-500 hover:bg-teal-50 transition-all group text-left"
              >
                <div className="bg-slate-100 group-hover:bg-teal-100 p-3 rounded-lg transition-colors">
                  <Factory className="w-6 h-6 text-slate-600 group-hover:text-teal-700 transition-colors" />
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-lg text-slate-900 group-hover:text-teal-800">
                    {plant.name}
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <Badge variant="outline" className="text-xs font-mono bg-white">{plant.code}</Badge>
                    {plant.location && (
                      <span className="text-xs text-slate-500 flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {plant.location}
                      </span>
                    )}
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-teal-600 transition-colors" />
              </button>
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <div className="flex items-center justify-center mb-4">
            <img 
              src="/metalman-logo.png" 
              alt="Metalman Logo" 
              className="h-20 w-auto object-contain"
            />
          </div>
          <CardTitle className="text-2xl font-bold text-center">Metalman Milkrun System</CardTitle>
          <CardDescription className="text-center">
            Sign in to access the planning system
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <label className="text-sm font-medium">Username</label>
              <div className="relative">
                <User className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                <Input
                  type="text"
                  name="username"
                  placeholder="Enter your username"
                  value={credentials.username}
                  onChange={handleInputChange}
                  required
                  className="pl-10"
                  disabled={loading}
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                <Input
                  type="password"
                  name="password"
                  placeholder="Enter your password"
                  value={credentials.password}
                  onChange={handleInputChange}
                  required
                  className="pl-10"
                  disabled={loading}
                />
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full" 
              disabled={loading}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Password Change Dialog */}
      <Dialog open={showPasswordChange} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-md" onPointerDownOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <div className="flex items-center justify-center mb-2">
              <div className="bg-orange-500 p-3 rounded-full">
                <KeyRound className="w-6 h-6 text-white" />
              </div>
            </div>
            <DialogTitle className="text-center">Change Your Password</DialogTitle>
            <DialogDescription className="text-center">
              Your password has been reset by an administrator. Please set a new password to continue.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>New Password *</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                <Input
                  type="password"
                  placeholder="Enter new password"
                  value={passwordChangeData.new_password}
                  onChange={(e) => setPasswordChangeData({ ...passwordChangeData, new_password: e.target.value })}
                  className="pl-10"
                  disabled={passwordChangeLoading}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Confirm Password *</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                <Input
                  type="password"
                  placeholder="Confirm new password"
                  value={passwordChangeData.confirm_password}
                  onChange={(e) => setPasswordChangeData({ ...passwordChangeData, confirm_password: e.target.value })}
                  className="pl-10"
                  disabled={passwordChangeLoading}
                />
              </div>
            </div>
            
            <p className="text-xs text-slate-500">Password must be at least 6 characters</p>
          </div>
          
          <DialogFooter>
            <Button 
              onClick={handlePasswordChange} 
              disabled={passwordChangeLoading}
              className="w-full"
            >
              {passwordChangeLoading ? 'Updating...' : 'Set New Password'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Session Exists Dialog */}
      <Dialog open={showSessionExistsDialog} onOpenChange={setShowSessionExistsDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="flex items-center justify-center mb-2">
              <div className="bg-orange-500 p-3 rounded-full">
                <User className="w-6 h-6 text-white" />
              </div>
            </div>
            <DialogTitle className="text-center">Already Logged In</DialogTitle>
            <DialogDescription className="text-center">
              You are already logged in on another device or browser. Do you want to logout from the other session and continue here?
            </DialogDescription>
          </DialogHeader>
          
          <div className="bg-amber-50 border border-amber-200 rounded-md p-3 my-4">
            <p className="text-sm text-amber-800">
              <strong>Note:</strong> Clicking "Login Here" will automatically log you out from the other device.
            </p>
          </div>
          
          <DialogFooter className="flex gap-2 sm:gap-2">
            <Button 
              variant="outline"
              onClick={() => setShowSessionExistsDialog(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleForceLogin}
              disabled={sessionExistsLoading}
              className="flex-1 bg-blue-600 hover:bg-blue-700"
            >
              {sessionExistsLoading ? 'Logging in...' : 'Login Here & Logout Other'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UnifiedLogin;
